import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { initiallyHiddenParticipants } from './replayParticipantName'
import { MapView, tangerRasterSource, useMap, type MapViewport } from '@wehunt/map'
import { useTranslation } from '../../i18n'
import { uiLocale } from '../../lib/dateFormat'
import { useHuntMapAppearance } from '../../lib/useHuntMapAppearance'
import { EMPTY_MAP_VIEW, groundFramePoints, pointsToBBox } from '../../lib/mapSeedView'
import { replayBounds } from '../../lib/replayEvents'
import { replayExtent } from '../../lib/replayInterpolate'
import {
  REPLAY_SKIP_STEPS_MS,
  REPLAY_SPEEDS,
  transientWindowMs,
  type ReplaySkipStepMs,
  type ReplaySpeed,
} from '../../lib/replayPlayback'
import type { ReplayProjection } from '../../lib/replayTrails'
import { COLORS } from '../../lib/theme'
import type { ReplayBundle, ReplayEventFilterKey, ReplayParticipant } from '../../lib/replayTypes'
import type { TeamMap } from '../../lib/types'
import { useBaseMapTileInfo } from '../../lib/useBaseMapTileInfo'
import { Icon } from '../Icon'
import { TeamMapLayers } from '../TeamMapLayers'
import { ReplayChooserPopover } from './ReplayChooserPopover'
import { REPLAY_CHROME } from './replayChrome'
import { ReplayFilterControls } from './ReplayFilterControls'
import { ReplayGamePinLayer } from './ReplayGamePinLayer'
import { ReplayMapPinLayer } from './ReplayMapPinLayer'
import { ReplayMarkerLayer } from './ReplayMarkerLayer'
import { ReplayPlaybackBar, skipStepName } from './ReplayPlaybackBar'
import { ReplayTrailCanvas } from './ReplayTrailCanvas'
import { useReplayClock } from './useReplayClock'

export interface HuntReplayViewerProps {
  bundle: ReplayBundle
  /** Closing is the host's business — it owns the route the viewer sits on. */
  onClose: () => void
}

type OpenSheet = 'speed' | 'skip' | null

// `MapView` reads `minZoom`/`maxZoom` once, at construction — see `ReportMap`'s
// identical comment for the full reasoning: the fetch behind `useBaseMapTileInfo`
// is async and can never answer before that first read. 1/19 matches the old
// `useReplayViewport`'s own DEFAULT_MIN_ZOOM/DEFAULT_MAX_ZOOM; the catalogue's real
// fetch bounds still live on the raster SOURCE, via `tangerRasterSource`'s
// `minzoom`/`maxzoom`.
const REPLAY_MIN_ZOOM = 1
const REPLAY_MAX_ZOOM = 19

/**
 * The replay viewer: a full-bleed animated map of an ended hunt with playback
 * controls, filters, and the events recorded along the way.
 *
 * Purely presentational. Everything it draws arrives in `bundle`, so the host owns
 * fetching, mapping and error states, and this component can be mounted in a test
 * with a hand-built hunt.
 *
 * One responsive layout rather than separate desktop and phone trees: share links
 * land on phones as often as not, and a second tree would be a second place for
 * every playback fix to be applied.
 */
export function HuntReplayViewer({ bundle, onClose }: HuntReplayViewerProps) {
  const huntMapAppearance = useHuntMapAppearance()
  const { t, i18n } = useTranslation('replay')

  const { startsAt, endsAt } = useMemo(() => replayExtent(bundle), [bundle])
  const bounds = useMemo(() => replayBounds(bundle), [bundle])
  const initialBounds = useMemo<[number, number, number, number] | null>(() => {
    if (bounds) return [bounds.minLon, bounds.minLat, bounds.maxLon, bounds.maxLat]
    // A replay with a ground but no recorded positions/pins has no trail to frame
    // on — fall back to the hunting ground so it opens on the ground rather than a
    // regional default, the same as the ended-hunt report map.
    return pointsToBBox(groundFramePoints(bundle.boundaryAreas ?? []))
  }, [bounds, bundle.boundaryAreas])
  const transientMs = useMemo(() => transientWindowMs(endsAt - startsAt), [startsAt, endsAt])

  const tileInfo = useBaseMapTileInfo()
  const baseSource = useMemo(
    () => (tileInfo ? tangerRasterSource({ ...tileInfo, widths: [] }) : null),
    [tileInfo],
  )

  // The hunt's own ground and drawn lines, under the trails, as a one-off TeamMap
  // so they go through the same `TeamMapLayers` pipeline every migrated map
  // surface uses — a replay's lines then look exactly like the team map's. Its
  // pins are not here: they are DOM markers, drawn below, as on the team map.
  const replayGroundMap = useMemo<TeamMap>(
    () => ({
      name: '',
      updated: '',
      boundaryAreas: [...(bundle.boundaryAreas ?? [])],
      subAreaPolygons: bundle.subAreaPolygons ? [...bundle.subAreaPolygons] : undefined,
      forbiddenPolygons: bundle.forbiddenPolygons ? [...bundle.forbiddenPolygons] : undefined,
      borderColor: bundle.teamBorderColor,
      objects: [],
      lines: bundle.lines.map((l) => ({
        id: l.id,
        name: l.name,
        kind: l.kind,
        points: l.points,
        color: l.color,
        width: l.width,
        style: l.style,
      })),
    }),
    [
      bundle.boundaryAreas,
      bundle.subAreaPolygons,
      bundle.forbiddenPolygons,
      bundle.teamBorderColor,
      bundle.lines,
    ],
  )

  const [liveZoom, setLiveZoom] = useState(REPLAY_MIN_ZOOM)
  const handleViewChange = useCallback((v: MapViewport) => setLiveZoom(v.zoom), [])

  // The live map instance, published up by `MapInstanceBridge` (rendered inside
  // `MapView`'s children, where `useMap()` resolves) — needed OUTSIDE that tree
  // because `ReplayTrailCanvas` is deliberately not one of `MapView`'s children
  // (see that component's file doc comment).
  const [map, setMap] = useState<ReturnType<typeof useMap>>(null)
  const handleMap = useCallback((m: ReturnType<typeof useMap>) => setMap(m), [])

  // The projection the trail canvas draws through, kept pointed at the live map's
  // own `project()` once one exists. `map.project([lon, lat])` returns
  // container-relative CSS pixels — the exact contract `ReplayProjection` already
  // promised, so the canvas's own drawing code needs no change at all.
  const projectRef = useRef<ReplayProjection>(() => [0, 0])
  useEffect(() => {
    if (!map) return
    projectRef.current = (lat, lon) => {
      const p = map.project([lon, lat])
      return [p.x, p.y]
    }
  }, [map])

  // Bumped once per MapLibre `move` event, which fires on every camera frame —
  // panning, zooming, or a programmatic `fitBounds`/`jumpTo`. The paused branch of
  // `ReplayTrailCanvas` reads this to know when to redraw; the playing branch's own
  // rAF loop already redraws every frame regardless of camera motion.
  const [viewEpoch, setViewEpoch] = useState(0)
  useEffect(() => {
    if (!map) return
    const bump = () => setViewEpoch((epoch) => epoch + 1)
    map.on('move', bump)
    return () => {
      map.off('move', bump)
    }
  }, [map])

  const mapContainerRef = useRef<HTMLDivElement>(null)
  const [mapSize, setMapSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 })
  useEffect(() => {
    const element = mapContainerRef.current
    if (!element) return
    const observer = new ResizeObserver(([entry]) => {
      setMapSize({ w: entry.contentRect.width, h: entry.contentRect.height })
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const clock = useReplayClock(startsAt, endsAt)

  // Anonymous carriers start deselected, as the shared-replay design shows them;
  // everyone else starts visible.
  const [hiddenParticipants, setHiddenParticipants] = useState<ReadonlySet<string>>(() =>
    initiallyHiddenParticipants(bundle.participants),
  )
  const [hiddenEvents, setHiddenEvents] = useState<ReadonlySet<ReplayEventFilterKey>>(new Set())
  const [openSheet, setOpenSheet] = useState<OpenSheet>(null)

  const toggleParticipant = useCallback((id: string) => {
    setHiddenParticipants((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const toggleEvent = useCallback((key: ReplayEventFilterKey) => {
    setHiddenEvents((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const setRoleVisibility = useCallback(
    (role: ReplayParticipant['role'], visible: boolean) => {
      const ids = bundle.participants.filter((p) => p.role === role).map((p) => p.id)
      setHiddenParticipants((current) => {
        const next = new Set(current)
        for (const id of ids) {
          if (visible) next.delete(id)
          else next.add(id)
        }
        return next
      })
    },
    [bundle.participants],
  )

  const setAllEventsVisible = useCallback((visible: boolean) => {
    setHiddenEvents(
      visible
        ? new Set<ReplayEventFilterKey>()
        : new Set<ReplayEventFilterKey>(['observed', 'downed', 'wounded', 'barking']),
    )
  }, [])

  // Keyboard shortcuts go through a ref so the listener always sees current state
  // without being torn down and re-added on every cursor tick.
  const shortcutsRef = useRef({
    toggle: clock.toggle,
    skip: clock.skip,
    close: onClose,
    openSheet,
    closeSheet: () => setOpenSheet(null),
  })
  shortcutsRef.current = {
    toggle: clock.toggle,
    skip: clock.skip,
    close: onClose,
    openSheet,
    closeSheet: () => setOpenSheet(null),
  }

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // Never steal a key from a control that legitimately wants it: Space on a
      // focused button, or an arrow on the scrubber.
      const target = event.target as HTMLElement | null
      const tag = target?.tagName
      const inControl = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'

      if (event.key === 'Escape') {
        // Escape dismisses the innermost thing that is open. Closing the whole
        // replay out from under an open chooser would throw away the viewer's
        // place in the hunt to answer a question they did not ask.
        const shortcuts = shortcutsRef.current
        if (shortcuts.openSheet !== null) shortcuts.closeSheet()
        else shortcuts.close()
        return
      }
      if (event.key === ' ' && !inControl && tag !== 'BUTTON') {
        event.preventDefault()
        shortcutsRef.current.toggle()
        return
      }
      if (event.key === 'ArrowRight' && !inControl) {
        event.preventDefault()
        shortcutsRef.current.skip(1)
        return
      }
      if (event.key === 'ArrowLeft' && !inControl) {
        event.preventDefault()
        shortcutsRef.current.skip(-1)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const locale = uiLocale(i18n.language)
  const huntDate = new Date(startsAt).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  return (
    <section
      data-testid="hunt-replay-viewer"
      aria-label={t('title')}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        flexDirection: 'column',
        background: REPLAY_CHROME.background,
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 16px',
          background: REPLAY_CHROME.topBarBackground,
          borderBottom: `1px solid ${REPLAY_CHROME.hairline}`,
          color: '#fff',
          flexShrink: 0,
        }}
      >
        {/* Says what this screen is. The map fills the frame, so without it there is
            nothing distinguishing a replay from any other map surface. */}
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            background: 'rgba(255,104,31,0.18)',
            color: '#ffb287',
            padding: '3px 8px',
            borderRadius: 4,
            flexShrink: 0,
          }}
        >
          <Icon name="play" size={13} />
          {t('replayBadge')}
        </span>
        <span
          style={{
            fontWeight: 700,
            fontSize: 15,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {bundle.name ?? t('untitledHunt')}
        </span>
        <span
          style={{
            fontSize: 13,
            color: REPLAY_CHROME.mutedText,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {bundle.teamName !== undefined ? `${bundle.teamName} · ${huntDate}` : huntDate}
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            aria-label={t('controls.close')}
            onClick={onClose}
            style={REPLAY_CHROME.iconButton}
          >
            <Icon name="close" size={18} />
          </button>
        </span>
      </header>

      <div
        ref={mapContainerRef}
        data-testid="replay-map"
        style={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          background: REPLAY_CHROME.background,
        }}
      >
        <MapView
          baseSource={baseSource}
          initialBounds={initialBounds}
          initialView={initialBounds ? undefined : EMPTY_MAP_VIEW}
          interactive
          // The replay already owns ArrowLeft/ArrowRight (skip) and Space
          // (play/pause) as window-level shortcuts — see the keydown handler
          // above. MapLibre's map canvas is focusable, and its own keyboard
          // handler independently pans on plain arrow keys (disableRotation
          // only strips Shift+arrow rotate/pitch, not this), so a focused
          // canvas would otherwise pan the camera AND skip the cursor on the
          // very same keypress. Disabling it here is the map-level half of
          // that fix.
          keyboard={false}
          minZoom={REPLAY_MIN_ZOOM}
          maxZoom={REPLAY_MAX_ZOOM}
          onViewChange={handleViewChange}
        >
          <TeamMapLayers
            map={replayGroundMap}
            appearance={huntMapAppearance}
            zoom={liveZoom}
            subAreaLabels={false}
          />

          {/* Before the game pins and participants so those draw above them. */}
          <ReplayMapPinLayer mapPins={bundle.mapPins} zoom={liveZoom} />

          <ReplayGamePinLayer bundle={bundle} cursor={clock.cursor} hiddenEvents={hiddenEvents} />

          <ReplayMarkerLayer
            bundle={bundle}
            cursor={clock.cursor}
            hiddenParticipants={hiddenParticipants}
            showBarking={!hiddenEvents.has('barking')}
            transientWindowMs={transientMs}
            showMessages={!hiddenEvents.has('messages')}
            speed={clock.speed}
          />

          <MapInstanceBridge onMap={handleMap} />

          {/* Zoom — right edge, vertically centred, the same placement every other map
              surface in the app uses. */}
          <ReplayZoomControls
            zoom={liveZoom}
            minZoom={REPLAY_MIN_ZOOM}
            maxZoom={REPLAY_MAX_ZOOM}
            zoomInLabel={t('controls.zoomIn')}
            zoomOutLabel={t('controls.zoomOut')}
          />
        </MapView>

        {/*
          Outside `MapView` deliberately: the canvas needs no map context, only the
          projection function `MapInstanceBridge` feeds it above — see the file doc
          comment on `ReplayTrailCanvas` for why it stays a canvas rather than
          becoming more MapLibre layers. Before the map exists there is nothing to
          project through yet, so the canvas stays unmounted rather than drawing
          through a placeholder that would silently draw nothing every frame.
        */}
        {map && (
          <ReplayTrailCanvas
            bundle={bundle}
            hiddenParticipants={hiddenParticipants}
            cursorRef={clock.cursorRef}
            projectRef={projectRef}
            size={mapSize}
            isPlaying={clock.isPlaying}
            viewEpoch={viewEpoch}
            cursor={clock.cursor}
          />
        )}

        {/* Filters — top-left, always present. They are the map's own controls, not a
            panel to be summoned: which hunters and dogs are drawn is part of reading
            the hunt. */}
        <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 8 }}>
          <ReplayFilterControls
            bundle={bundle}
            hiddenParticipants={hiddenParticipants}
            hiddenEvents={hiddenEvents}
            onToggleParticipant={toggleParticipant}
            onToggleEvent={toggleEvent}
            onSetRoleVisibility={setRoleVisibility}
            onSetAllEventsVisible={setAllEventsVisible}
          />
        </div>
      </div>

      <ReplayPlaybackBar
        startsAt={startsAt}
        endsAt={endsAt}
        cursor={clock.cursor}
        isPlaying={clock.isPlaying}
        speed={clock.speed}
        skipStepMs={clock.skipStepMs}
        onToggle={clock.toggle}
        onSeek={clock.seek}
        onSkip={clock.skip}
        onOpenSpeedChooser={() => setOpenSheet((current) => (current === 'speed' ? null : 'speed'))}
        onOpenSkipChooser={() => setOpenSheet((current) => (current === 'skip' ? null : 'skip'))}
        speedChooserOpen={openSheet === 'speed'}
        skipChooserOpen={openSheet === 'skip'}
        speedChooser={
          openSheet === 'speed' ? (
            <ReplayChooserPopover
              title={t('speed.title')}
              options={REPLAY_SPEEDS.map((value) => ({
                value,
                top: t('speed.value', { value }),
              }))}
              selected={clock.speed}
              onSelect={(value) => clock.setSpeed(value as ReplaySpeed)}
              onClose={() => setOpenSheet(null)}
            />
          ) : undefined
        }
        skipChooser={
          openSheet === 'skip' ? (
            <ReplayChooserPopover
              title={t('skip.title')}
              options={REPLAY_SKIP_STEPS_MS.map((value) => ({
                value,
                // Split so the number reads large with its unit beneath, as on mobile.
                top: String(value < 60_000 ? value / 1000 : value / 60_000),
                bottom: value < 60_000 ? t('units.sec') : t('units.min'),
              }))}
              selected={clock.skipStepMs}
              onSelect={(value) => clock.setSkipStepMs(value as ReplaySkipStepMs)}
              onClose={() => setOpenSheet(null)}
            />
          ) : undefined
        }
      />
    </section>
  )
}

/**
 * Publishes the live map instance up to the viewer. `ReplayTrailCanvas` draws on
 * an absolute overlay OUTSIDE `MapView`'s own children (it needs no map context —
 * only a projection function, see that component's file doc comment), so the only
 * way the viewer can hand it a live `map.project()` is through a value threaded
 * out via a child that DOES sit where `useMap()` resolves. Local to this file: no
 * other surface needs a map instance outside its own `MapView` tree.
 */
function MapInstanceBridge({ onMap }: { onMap: (map: ReturnType<typeof useMap>) => void }) {
  const map = useMap()
  useEffect(() => {
    onMap(map)
  }, [map, onMap])
  return null
}

/**
 * The map's own zoom stack, rendered inside `MapView`'s children so `useMap()`
 * resolves to the live map instance — the same pattern `ReportMap`'s and
 * `StandsMap`'s own zoom controls use. Disabled at this map's fixed 1/19 bounds,
 * the same bounds `MapView` itself is constructed with.
 */
function ReplayZoomControls({
  zoom,
  minZoom,
  maxZoom,
  zoomInLabel,
  zoomOutLabel,
}: {
  zoom: number
  minZoom: number
  maxZoom: number
  zoomInLabel: string
  zoomOutLabel: string
}) {
  const map = useMap()
  return (
    <div
      style={{
        position: 'absolute',
        right: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 8,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <MapRoundButton label={zoomInLabel} onClick={() => map?.zoomIn()} disabled={zoom >= maxZoom}>
        <Icon name="zoom-in" size={20} />
      </MapRoundButton>
      <MapRoundButton label={zoomOutLabel} onClick={() => map?.zoomOut()} disabled={zoom <= minZoom}>
        <Icon name="zoom-out" size={20} />
      </MapRoundButton>
    </div>
  )
}

/**
 * A circular card button floating on the map — the shape the app's other map
 * surfaces use for their controls.
 */
function MapRoundButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="wh-card"
      style={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        cursor: disabled === true ? 'default' : 'pointer',
        color: disabled === true ? COLORS.outline : COLORS.onSurface,
      }}
    >
      {children}
    </button>
  )
}
